(() => {
  const ENDPOINT = "/apps/dtf-builder/api/create-draft-order-from-cart";
  let busy = false;

  const isCheckoutSubmit = (event) => {
    const submitter = event.submitter;
    if (submitter) return submitter.name === "checkout";
    return event.target?.id === "cart" || event.target?.id === "CartDrawer-Form";
  };

  // Products whose price the builder calculates (applied on a draft order).
  // Gang sheets are charged their variant price and must never be listed here.
  const CUSTOM_PRICED_TYPES = new Set(["die-cut-stickers", "spot-uv-stickers"]);

  // Mirrors the app's rule (custom-priced-products.server.js):
  // - a line WITH _builder_type is custom-priced only for the sticker products;
  // - a line WITHOUT one (Die Cut's standalone cart flow) is Die Cut when its
  //   file is under designs/die-cut/ or it carries _Calculated_Price.
  // Reference_Code is not a signal any more — every product sets one.
  const customPrice = (item) => {
    const props = item?.properties || {};
    const type = String(props._builder_type || "").toLowerCase().trim();
    const fileRef = String(
      props._production_key || props._production_url || props.Design_URL || "",
    );
    const marked = type
      ? CUSTOM_PRICED_TYPES.has(type)
      : /designs\/die-cut\//i.test(fileRef) ||
        (props._Calculated_Price != null && String(props._Calculated_Price).trim() !== "");
    if (!marked) return null;
    const value = Number(props._custom_price ?? props._Calculated_Price);
    return Number.isFinite(value) && value >= 0 ? value : NaN;
  };

  // The cart form posts to /cart and only advances to checkout when the
  // `checkout` submit button's name/value are part of the submission.
  // form.submit() drops the submitter, so a plain re-submit silently
  // re-rendered the cart instead of continuing.
  const submitNatively = (form, submitter) => {
    if (!form || typeof form.submit !== "function") {
      window.location.assign("/checkout");
      return;
    }
    if (!submitter?.name) {
      window.location.assign("/checkout");
      return;
    }
    const passthrough = document.createElement("input");
    passthrough.type = "hidden";
    passthrough.name = submitter.name;
    passthrough.value = submitter.value || "";
    form.appendChild(passthrough);
    form.submit();
  };

  async function createDraftFromCart() {
    const cartResponse = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!cartResponse.ok) throw new Error("Could not read the cart");
    const cart = await cartResponse.json();
    const customLines = cart.items.filter((item) => customPrice(item) !== null);
    if (!customLines.length) return null;
    if (customLines.some((item) => Number.isNaN(customPrice(item)))) {
      throw new Error("A custom sticker is missing its calculated price");
    }

    const response = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        shop: window.Shopify?.shop || "",
        currencyCode: cart.currency,
        items: cart.items.map((item) => ({
          variantId: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
        })),
      }),
    });
    const text = await response.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch {
      throw new Error(`Draft checkout returned ${response.status}`);
    }
    if (!response.ok || !result?.invoiceUrl) {
      throw new Error(result?.error || "Could not create draft checkout");
    }
    return result.invoiceUrl;
  }

  document.addEventListener(
    "submit",
    async (event) => {
      if (!isCheckoutSubmit(event) || busy) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      busy = true;
      const submitter = event.submitter;
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute("aria-busy", "true");
      }
      try {
        const invoiceUrl = await createDraftFromCart();
        if (invoiceUrl) {
          window.location.assign(invoiceUrl);
          return;
        }
        // A cart with no custom sticker should use native Shopify checkout.
        submitNatively(event.target, submitter);
      } catch (error) {
        console.error("[AnyNest checkout]", error);
        alert(`Checkout could not be prepared: ${error.message || error}`);
        busy = false;
        if (submitter) {
          submitter.disabled = false;
          submitter.removeAttribute("aria-busy");
        }
      }
    },
    true,
  );
})();
